// NIGHT-DIRECTSTAFF-2026-10-23b §2: duplicate candidates -- two candidate records with the same phone (last 10 digits) or the
// same email (any case). "Possible duplicate" on the candidate; Merge keeps the OLDER record (created_at, then the lower id)
// and the database moves the other's activity, placements, documents, drafts, messages and offers to it, marks the other
// merged_into, and deletes nothing (migrations/2026-10-23-candidates-merged-into.sql: candidate_merge). It refuses when the
// other has an onboarding packet, exams or health rows -- a person decides those.

export const MERGE_COLS = ['merged_into', 'merged_at', 'merged_by'];
export const MERGE_LOG_TYPE = 'merge';
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const national = (v) => { const d = String(v || '').replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d.length >= 10 ? d.slice(-10) : ''; };
const emailKey = (v) => { const e = s(v).toLowerCase(); return e.includes('@') ? e : ''; };
const t = (iso) => { const x = new Date(iso).getTime(); return Number.isNaN(x) ? Infinity : x; };

export const isMergedCandidate = (c) => !!(c && s(c.merged_into));
/** 'phone' | 'email' | 'phone and email' | '' */
export function sameOn(a, b) {
  if (!a || !b || a.id === b.id) return '';
  const p = !!national(a.phone) && national(a.phone) === national(b.phone);
  const e = !!emailKey(a.email) && emailKey(a.email) === emailKey(b.email);
  return p && e ? 'phone and email' : p ? 'phone' : e ? 'email' : '';
}
/** -> { older, newer } (created_at; a tie keeps the lower id) -- the database's rule too */
export function olderNewer(a, b) {
  const ta = t(a.created_at); const tb = t(b.created_at);           // no date sorts last; two missing dates are a tie (not NaN)
  return ta < tb || (ta === tb && Number(a.id) < Number(b.id)) ? { older: a, newer: b } : { older: b, newer: a };
}
/** The other (not merged) candidates that share a phone or email with `c`, oldest first: [{ other, on }] */
export function possibleDuplicates(c, candidates) {
  if (!c || isMergedCandidate(c)) return [];
  return (candidates || []).filter((o) => !isMergedCandidate(o)).map((o) => ({ other: o, on: sameOn(c, o) })).filter((x) => x.on)
    .sort((a, b) => olderNewer(a.other, b.other).older === a.other ? -1 : 1);
}
/** Every pair, ids only, [keep, other, on] -- the report's backfill list (the CRM never merges by itself) */
export function duplicatePairs(candidates) {
  const live = (candidates || []).filter((c) => !isMergedCandidate(c));
  const out = [];
  for (let i = 0; i < live.length; i += 1) for (let j = i + 1; j < live.length; j += 1) {
    const on = sameOn(live[i], live[j]);
    if (on) { const { older, newer } = olderNewer(live[i], live[j]); out.push([older.id, newer.id, on]); }
  }
  return out.sort((a, b) => Number(a[0]) - Number(b[0]) || Number(a[1]) - Number(b[1]));
}
/** The candidate a merged id now lives in (one hop; the database refuses chains). */
export function resolveMerged(id, candidates, merged) {
  const hit = (candidates || []).find((c) => String(c.id) === String(id));
  if (hit) return hit;
  const m = (merged || []).find((c) => String(c.id) === String(id));
  return m ? (candidates || []).find((c) => String(c.id) === String(m.merged_into)) || null : null;
}

const WHY = {
  'candidate:keep_the_older': 'Merge keeps the older record. Open the newer one to merge it into this one.',
  'candidate:already_merged': 'One of them is already merged. Refresh.',
  'candidate:has_merged_into_it': 'Another record was merged into that one first; ask the hub.',
  'candidate:not_found': 'One of them is no longer there. Refresh.',
  'candidate:bad_pair': 'Choose two different records.',
};
/** The database's refusal in plain words */
export function mergeError(e) {
  const m = String((e && e.message) || e || '');
  const blocked = /candidate:merge_blocked:([a-z_]+)/.exec(m);
  if (blocked) return `Not merged: the newer record has ${blocked[1].replace(/^onboarding_/, 'onboarding ').replace(/_/g, ' ')} (its onboarding, exams or health). Nothing changed -- ask the hub to decide.`;
  const k = Object.keys(WHY).find((x) => m.includes(x));
  return k ? WHY[k] : m || 'Could not merge';
}
export async function probeCandidateMerge(client) {
  try { const { error } = await client.from('candidates').select(MERGE_COLS.join(','), { head: true }).limit(0); return !error; } catch { return false; }
}
export async function mergeCandidates(client, keepId, otherId) {
  const { data, error } = await client.rpc('candidate_merge', { p_keep: keepId, p_other: otherId });
  if (error) throw new Error(mergeError(error));
  return data;
}
/** "Candidate #75 merged into this one by staff@x: moved 3 activity log, ..." -> { other, by, moved } | null */
export function parseMergeNote(note) {
  const m = /^Candidate #(\d+) merged into this one by ([^:(]+?)(?:: moved (.*)| \(nothing to move\))?$/.exec(s(note));
  return m ? { other: Number(m[1]), by: m[2].trim(), moved: m[3] || '' } : null;
}
